import { DecisionRefusedError, ReadFailedError } from '../api/client';
import { ActRefusedError } from '../api/pipeline';
import type { Me } from './api';

/**
 * Losing a session mid-task, and what survives it
 * (docs/DECISIONS.md ZD-118 §4).
 *
 * A session ends after thirty minutes idle or twelve hours outright, on the
 * record's clock and not the browser's (ZD-117 §4), so a reviewer forty
 * findings into an assessment can meet a 401 on the next thing they record.
 * What is at risk is small and exact: every decision already recorded is the
 * server's, and only the draft in the form is not. So the workspace stays
 * mounted, a sign-in panel appears above it, and the draft stays where it is.
 *
 * ## Only a 401 is a lost session
 *
 * A 403 is a role the record does not give this person; a 501 is a deployment
 * that cannot authenticate anybody; a 409 is the assessment's state. None of
 * those is cured by signing in, and a panel that asked for a password over any
 * of them would be asking for the wrong thing. The status is the API's whole
 * statement of the kind of refusal, and this reads exactly that.
 *
 * ## A different person signing in drops the draft
 *
 * The panel does not know who will type into it. If the person who signs in
 * is not the person who was signed in, a draft the first wrote would be
 * recorded — and audited, by the record's own stamp — as the second's
 * judgement. So the workspace re-reads `/v1/me` after every re-authentication
 * and remounts, discarding the draft, when the `user_id` differs; and says so.
 * The comparison is on `user_id` and nothing else, because a display name or
 * an email can be changed by an administrator without the person changing.
 */

export function sessionLost(error: unknown): boolean {
  if (error instanceof ReadFailedError) return error.status === 401;
  if (error instanceof DecisionRefusedError) return error.status === 401;
  if (error instanceof ActRefusedError) return error.status === 401;
  return false;
}

/** The API's own sentence from a refusal, for a page to show in those words. */
export function apiSentence(error: unknown): string {
  if (
    error instanceof ReadFailedError ||
    error instanceof DecisionRefusedError ||
    error instanceof ActRefusedError
  ) {
    return error.detail;
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * Whether the person who just signed in is a different person from the one
 * who was signed in. With nobody known before there is nobody to differ from
 * — a cold load's first sign-in protects no draft — so that is `false`.
 */
export function signatoryChanged(
  before: Pick<Me, 'user_id'> | null,
  after: Pick<Me, 'user_id'>,
): boolean {
  return before !== null && before.user_id !== after.user_id;
}

export const SESSION_LOST_SENTENCE =
  'Your session has ended. Sign in above and record the decision again — what you wrote is ' +
  'still here, and every decision you already recorded is kept.';

export const SIGNATORY_CHANGED_SENTENCE =
  'A different person signed in, so the decision that was being written has been discarded ' +
  'rather than recorded under their name.';
